import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import React, { useState } from 'react';
import styled from 'styled-components';

const QuantityContainer = styled('div')(() => ({
    display: ' block',
    width: '110px',
    position: 'relative',
    marginRight: 0,
}));

const ButtonContainer = styled('button')(() => ({
    width: '32px',
    display: 'block',
    position: 'absolute',
    top: '0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    height: '45px',
    '&.minus': {
        left: '0',
        right: 'auto',
    },
    '&.plus': {
        left: 'auto',
        right: '0',
    },
}));

const QuantityInput = styled('input')(() => ({
    width: '100%',
    fontSize: '14px',
    textAlign: 'center',
    paddingTop: '10px',
    paddingBottom: '10px',
    minHeight: '42px',
    border: '1px solid #c7c7c7',
    borderRadius: '5px',
    appearance: 'none',
    '&:focus': {
        border: '1px solid #c7c7c7',
    },
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        appearance: 'none',
        margin: '0',
    },
}));

export const QuantitySelector: React.FC<QuantitySelectorProps> = (props) => {
    const {
        qtyChanged,
        minOrderQuantity,
        minOrderMultiplier,
        defaultQty,
        flat,
        disabled,
    } = props;
    const [qty, setQty] = useState<number>(
        defaultQty ? defaultQty : minOrderQuantity > 0 ? minOrderQuantity : 1
    );
    const increaseQty = () => {
        const qtySum =
            minOrderQuantity > 0
                ? minOrderMultiplier
                    ? qty + minOrderQuantity
                    : qty + 1
                : qty + 1;
        setQty(qtySum);
        qtyChanged(qtySum);
    };

    const decreaseQty = () => {
        const qtySum =
            minOrderQuantity > 0
                ? minOrderMultiplier
                    ? qty - minOrderQuantity
                    : qty - 1
                : qty - 1;
        if (qtySum > 0) {
            setQty(qtySum);
            qtyChanged(qtySum);
        }
    };

    const handleChange = (value: number) => {
        setQty(value < 1 ? 1 : value);
        if (event.type == 'blur') {
            if (minOrderQuantity > 0) {
                if (minOrderMultiplier) {
                    const diff = value % minOrderQuantity;
                    if (diff == 0) {
                        qtyChanged(value);
                    } else {
                        if (value < minOrderQuantity) {
                            setQty(minOrderQuantity);
                            qtyChanged(minOrderQuantity);
                        } else {
                            setQty(value - diff);
                            qtyChanged(value - diff);
                        }
                    }
                } else {
                    if (value < minOrderQuantity) {
                        setQty(minOrderQuantity);
                        qtyChanged(minOrderQuantity);
                    } else {
                        qtyChanged(value);
                    }
                }
            } else {
                qtyChanged(value);
            }
        }
    };
    return (
        <QuantityContainer>
            <ButtonContainer className={'minus'} onClick={decreaseQty}>
                <RemoveIcon
                    sx={{
                        alignSelf: 'center',
                        width: '20px',
                        height: '20px',
                        ...(flat ? { color: 'GrayText' } : null),
                    }}
                />
            </ButtonContainer>
            <QuantityInput
                min={
                    minOrderQuantity && minOrderQuantity > 0
                        ? minOrderQuantity
                        : 1
                }
                value={qty}
                type={'text'}
                onChange={(e) => handleChange(Number(e.target.value))}
                onBlur={(e) => handleChange(Number(e.target.value))}
                disabled={disabled}
                sx={
                    flat
                        ? {
                              minWidth: '6rem',
                          }
                        : { minHeight: '40px' }
                }
            />
            <ButtonContainer className={'plus'} onClick={increaseQty}>
                <AddIcon
                    sx={{
                        alignSelf: 'center',
                        width: '20px',
                        height: '20px',
                        ...(flat ? { color: 'GrayText' } : null),
                    }}
                />
            </ButtonContainer>
        </QuantityContainer>
    );
};

type QuantitySelectorProps = {
    qtyChanged: (qty: number) => void;
    minOrderMultiplier: boolean;
    minOrderQuantity: number;
    defaultQty?: number;
    flat?: boolean;
    disabled?: boolean;
};
